import {
  AdopterDto,
  AdoptionAttemptDto,
  AdoptionAttemptDtoStatus,
  AnimalAdoptionDtoStatus,
} from '../interactors/gen/backendClient'

interface AdoptionAttemptContext {
  adopter: AdopterDto
  attempt: AdoptionAttemptDto
}

const getRelevantAttempt = (
  attempts: AdoptionAttemptDto[],
  adoptionStatus: AnimalAdoptionDtoStatus | undefined
): AdoptionAttemptDto | undefined => {
  const latestAttempts = [...attempts].reverse()

  if (latestAttempts.length === 0) {
    return undefined
  }

  if (adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable) {
    return (
      latestAttempts.find(
        (attempt) =>
          attempt.done !== true &&
          attempt.archived !== true &&
          attempt.status !== AdoptionAttemptDtoStatus.SuspendedByCustomer &&
          attempt.status !== AdoptionAttemptDtoStatus.SuspendedByUser
      ) || latestAttempts[0]
    )
  }

  if (adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted) {
    return (
      latestAttempts.find(
        (attempt) =>
          attempt.done === true || attempt.archived === true || attempt.status === AdoptionAttemptDtoStatus.Done
      ) || latestAttempts[0]
    )
  }

  return latestAttempts[0]
}

const getRelevantAttemptContext = (
  contexts: AdoptionAttemptContext[],
  adoptionStatus: AnimalAdoptionDtoStatus | undefined
): AdoptionAttemptContext | undefined => {
  const relevantAttempt = getRelevantAttempt(
    contexts.map(({ attempt }) => attempt),
    adoptionStatus
  )

  return relevantAttempt ? contexts.find(({ attempt }) => attempt.id === relevantAttempt.id) : undefined
}

export const getRelevantAttemptForAnimal = (
  attempts: AdoptionAttemptDto[] | undefined,
  animalId: string,
  adoptionStatus: AnimalAdoptionDtoStatus | undefined
): AdoptionAttemptDto | undefined => {
  return getRelevantAttempt(
    (attempts ?? []).filter((attempt) => attempt.animalId === animalId),
    adoptionStatus
  )
}

export const getRelevantAdoptionContextForAnimal = (
  adopters: AdopterDto[],
  animalId: string,
  adoptionStatus: AnimalAdoptionDtoStatus | undefined,
  preferredAdopterId?: string
): AdoptionAttemptContext | undefined => {
  const contexts = adopters.flatMap((adopter) =>
    (adopter.adoptionAttempts ?? [])
      .filter((attempt) => attempt.animalId === animalId)
      .map((attempt) => ({
        adopter,
        attempt,
      }))
  )

  if (contexts.length === 0) {
    return undefined
  }

  const preferredContexts = preferredAdopterId
    ? contexts.filter(({ adopter }) => adopter.id === preferredAdopterId)
    : []

  return getRelevantAttemptContext(preferredContexts.length > 0 ? preferredContexts : contexts, adoptionStatus)
}
